/**
 * Blackwork embroidery charts — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_stitch_charts`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const blackworkEmbroideryChartsPack: ToilePack = {
    slug: 'blackwork-embroidery-charts',
    category: 'craft',
    name: 'Blackwork embroidery charts',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 1024, h: 1024},
    readings: [
        {key: 'fills', label: 'Fill patterns', subject: 'a blackwork chart of nine small fill patterns in a three-by-three sampler, each a different geometric repeat of straight stitches over a faint grid'},
        {key: 'border', label: 'Border', subject: 'a blackwork chart of a band border of interlaced strapwork and small crosses in double running stitch, the repeat bracketed along the bottom'},
        {key: 'motif', label: 'Motif', subject: 'a blackwork chart of a strawberry and leaf filled with geometric patterns of different density, a solid outline around the shape and the grid faint behind'},
    ],
    styles: [
        {key: 'grid', label: 'Counted grid', style: 'straight black stitch lines drawn over a faint grey counting grid, each stitch exactly one or two squares long', palette: 'black ink only on pure white', framing: 'centered on the sheet with an even white margin all round'},
        {key: 'dense', label: 'Dense fill', style: 'heavy dense geometric fill with tightly packed stitch lines, strong black against white', palette: 'black ink only on pure white', framing: 'centered on the sheet with an even white margin all round'},
        {key: 'airy', label: 'Airy fill', style: 'light open geometric fill with wide spacing and thin stitch lines, mostly white', palette: 'black ink only on pure white', framing: 'centered on the sheet with an even white margin all round'},
    ],
    directions: ['heart', 'a bird', 'collar'],
    directionHint: 'try: heart',
    seo: {
        title: 'Blackwork embroidery charts: fills, border, motif',
        metaDescription: 'Nine blackwork embroidery charts: fill samplers, a strapwork border and a strawberry motif, in dense or airy counted fills.',
        h1: 'Blackwork embroidery charts: fill samplers, a border and a motif',
        tagline: 'Counted charts of straight stitches in one dark thread, drawn over an even-weave grid.',
        intro: 'Blackwork is counted embroidery in a single dark thread, worked in double running stitch into geometric fills. It dates from Tudor England, where it trimmed collars and cuffs. These nine charts cover a sampler of fills, a strapwork border and a strawberry motif. Each comes as a counted grid, a dense fill or an airy fill. Type a different motif and the same counted grid carries it.',
        useCases: [
            'a sampler of fill patterns',
            'a collar or cuff trim',
            'a bookmark border',
            'a motif for a small framed piece',
        ],
        faq: [
            {q: 'What is a counted chart?', a: 'Each stitch covers one or two squares of an even grid, so you count the squares and stitch to the chart.'},
            {q: 'What is the difference between dense and airy?', a: 'Dense fill packs the stitch lines close for a strong dark area. Airy fill spaces them wide, so the cloth shows between them.'},
            {q: 'Is blackwork worked in more than one thread?', a: 'No. Blackwork uses one dark thread, and the charts show black ink on white.'},
        ],
    },
}
